import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { getCategoryGroups, getFooterCategories, shortCategoryName } from "@/data/category-groups";
import { FANCY_STYLE_COUNT } from "@/data/generator-stats.generated";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

const NAV_LINK =
  "rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground";
const NAV_LINK_ACTIVE = "rounded-md px-3 py-1.5 text-sm bg-secondary text-foreground";

// Trigger styled to blend into the glass header (the vendored style is bg-background).
const TRIGGER = cn(
  navigationMenuTriggerStyle(),
  "h-auto bg-transparent px-3 py-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground",
  "focus:bg-secondary focus:text-foreground data-[state=open]:bg-secondary data-[state=open]:text-foreground",
);

const TOOLS = [
  { to: "/font-generator", label: "Font Generator", desc: `${FANCY_STYLE_COUNT} fancy fonts, zalgo, upside-down & more` },
  { to: "/decorative", label: "Decorative Symbols", desc: "Borders, dividers, loading bars & combos" },
  { to: "/kaomoji", label: "Kaomoji & Text Faces", desc: "Japanese emoticons by mood" },
  { to: "/lenny-faces", label: "Lenny Faces", desc: "( ͡° ͜ʖ ͡°) text emoticons to copy" },
  { to: "/draw", label: "Draw to Find", desc: "Sketch a symbol to look it up" },
  { to: "/export", label: "Bulk Export", desc: "Download symbols as SVG/PNG" },
  { to: "/pack", label: "Symbol Packs", desc: "Save & share curated sets" },
] as const;

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const groups = getCategoryGroups();

  return (
    <header className="sticky top-0 z-40 glass">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link
          to="/"
          onClick={() => setOpen(false)}
          className="flex items-center gap-2 font-display text-base font-bold sm:text-lg"
        >
          <span className="text-gradient text-2xl leading-none">✦</span>
          <span>
            CoolSymbols<span className="text-muted-foreground">.cool</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className={TRIGGER}>Categories</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="w-[min(92vw,52rem)] p-4">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
                      {groups.map((g) => (
                        <div key={g.title}>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {g.title}
                          </p>
                          <ul className="space-y-0.5">
                            {g.items.map((c) => (
                              <li key={c.slug}>
                                <NavigationMenuLink asChild>
                                  <Link
                                    to="/categories/$slug"
                                    params={{ slug: c.slug }}
                                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground/80 hover:bg-secondary hover:text-foreground"
                                  >
                                    <span className="w-5 text-center text-base leading-none">{c.emoji}</span>
                                    <span className="truncate">{shortCategoryName(c.name)}</span>
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 border-t border-border/60 pt-3">
                      <NavigationMenuLink asChild>
                        <Link
                          to="/categories"
                          className="inline-flex rounded-md px-2 py-1.5 text-sm font-medium text-foreground hover:bg-secondary"
                        >
                          Browse all categories →
                        </Link>
                      </NavigationMenuLink>
                    </div>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className={TRIGGER}>Tools</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="w-[20rem] p-3">
                    {TOOLS.map((t) => (
                      <li key={t.to}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={t.to}
                            className="block rounded-md px-3 py-2 hover:bg-secondary"
                          >
                            <span className="text-sm font-medium text-foreground">{t.label}</span>
                            <span className="mt-0.5 block text-xs text-muted-foreground">{t.desc}</span>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          <Link to="/use-cases" className={NAV_LINK} activeProps={{ className: NAV_LINK_ACTIVE }}>
            Use cases
          </Link>
        </nav>

        <div className="hidden items-center gap-3 text-sm md:flex">
          <Link to="/my-symbols" className="text-muted-foreground hover:text-foreground" activeProps={{ className: "text-foreground" }}>
            ★ Favorites
          </Link>
          <Link to="/about" className="text-muted-foreground hover:text-foreground">
            About
          </Link>
          <ThemeToggle />
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-surface/60 text-foreground md:hidden"
        >
          <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
          {open ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile menu panel */}
      {open && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-border/60 md:hidden">
          <nav className="mx-auto max-w-7xl px-4 py-3">
            {/* Divider lines between each category group so the sections read
                as distinct blocks, not one undifferentiated list. */}
            <div className="divide-y divide-border/60">
              {groups.map((g) => (
                <div key={g.title} className="py-3 first:pt-0">
                  <p className="mb-1 px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {g.title}
                  </p>
                  <div className="grid grid-cols-2 gap-1">
                    {g.items.map((c) => (
                      <Link
                        key={c.slug}
                        to="/categories/$slug"
                        params={{ slug: c.slug }}
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground/90 hover:bg-secondary"
                      >
                        <span className="w-5 text-center text-base leading-none">{c.emoji}</span>
                        <span className="truncate">{shortCategoryName(c.name)}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 grid gap-1 border-t border-border/60 pt-3">
              <Link to="/categories" onClick={() => setOpen(false)} className="rounded-md border border-border bg-surface/60 px-3 py-2 text-center text-sm font-medium hover:bg-secondary">
                All categories
              </Link>
              {TOOLS.map((t) => (
                <Link key={t.to} to={t.to} onClick={() => setOpen(false)} className="rounded-md border border-border bg-surface/60 px-3 py-2 text-center text-sm font-medium hover:bg-secondary">
                  {t.label}
                </Link>
              ))}
              <Link to="/use-cases" onClick={() => setOpen(false)} className="rounded-md border border-border bg-surface/60 px-3 py-2 text-center text-sm font-medium hover:bg-secondary">
                Use cases
              </Link>
              <Link to="/my-symbols" onClick={() => setOpen(false)} className="rounded-md border border-border bg-surface/60 px-3 py-2 text-center text-sm font-medium hover:bg-secondary">
                ★ Favorites
              </Link>
              <Link to="/about" onClick={() => setOpen(false)} className="rounded-md px-3 py-2 text-center text-sm text-muted-foreground hover:text-foreground">
                About
              </Link>
              <div className="mt-1 flex justify-center">
                <ThemeToggle />
              </div>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  const featured = getFooterCategories();

  return (
    <footer className="mt-24 border-t border-border/60">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 font-display text-lg font-bold">
              <span className="text-gradient text-2xl">✦</span>
              CoolSymbols.cool
            </div>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">
              The fastest, most modern symbol library on the web. Click. Copy. Paste anywhere.
            </p>
          </div>

          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Categories</h2>
            <ul className="space-y-1 text-sm">
              {featured.map((c) => (
                <li key={c.slug}>
                  <Link
                    to="/categories/$slug"
                    params={{ slug: c.slug }}
                    className="flex items-center gap-1.5 text-foreground/80 hover:text-foreground"
                  >
                    <span className="text-base leading-none">{c.emoji}</span>
                    <span>{shortCategoryName(c.name)}</span>
                  </Link>
                </li>
              ))}
              <li className="pt-1">
                <Link to="/categories" className="font-medium text-foreground hover:underline">
                  All categories →
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Site</h2>
            <ul className="space-y-1 text-sm">
              <li><Link to="/" className="text-foreground/80 hover:text-foreground">Home</Link></li>
              <li><Link to="/use-cases" className="text-foreground/80 hover:text-foreground">Use Cases</Link></li>
              <li><Link to="/font-generator" className="text-foreground/80 hover:text-foreground">Font Generator</Link></li>
              <li><Link to="/decorative" className="text-foreground/80 hover:text-foreground">Decorative Symbols</Link></li>
              <li><Link to="/kaomoji" className="text-foreground/80 hover:text-foreground">Kaomoji & Text Faces</Link></li>
              <li><Link to="/lenny-faces" className="text-foreground/80 hover:text-foreground">Lenny Faces</Link></li>
              <li><Link to="/draw" className="text-foreground/80 hover:text-foreground">Draw to Find</Link></li>
              <li><Link to="/export" className="text-foreground/80 hover:text-foreground">Bulk Export</Link></li>
              <li><Link to="/pack" className="text-foreground/80 hover:text-foreground">Symbol Packs</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-border/60 pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          {/* Legal nav first in the DOM so it sits right under the divider on
              mobile (clearly visible, not buried under the copyright line);
              reordered after the copyright on ≥sm via flex order. */}
          <nav aria-label="Legal" className="order-1 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium text-foreground/70 sm:order-2 sm:text-xs">
            <Link to="/about" className="hover:text-foreground">About</Link>
            <Link to="/contact" className="hover:text-foreground">Contact</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
          </nav>
          <p className="order-2 sm:order-1">© {new Date().getFullYear()} CoolSymbols.cool. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
